// A play that lives in this browser (docs/05-Tools/Level Designer.md,
// Playtest): the level designer hands over an authoring record, and this
// answers start, word, remove and finish exactly as the Worker would, with
// the shared core doing the replay, the checks and the scoring. The
// dictionary is still the Worker's, one public call per word.
// Nothing is recorded anywhere.
import {
  acceptSelection,
  assemblageComplete,
  blankIndexFor,
  blanksFor,
  checkSubmission,
  createPlayState,
  puzzleComplete,
  removeConstellation,
  replayMoves,
  scoreConstellation,
  scorePlay,
  spelledWord,
  starsTotal,
  starsUsed,
  tapNode,
  toPlayArtifact,
  type AuthoringLevel,
  type Move,
  type PlayLevel,
  type PlayState,
} from '@wits/core';
import { api, ApiError } from '../api/client';
import type { FinishResponse, PlayRecord, RemoveResponse, WordResponse } from '../api/types';
import type { PlayBackend } from './backend';

export const PLAYTEST_ID = 'playtest';

function refuse(status: number, code: string, message: string, extra: Record<string, unknown> = {}): never {
  throw new ApiError(status, { error: code, message, outcome: code, ...extra });
}

export function createLocalBackend(authoring: AuthoringLevel): PlayBackend & { reset(): void } {
  const level: PlayLevel = toPlayArtifact(authoring, (authoring.published?.version ?? 0) + 1);
  const solutions = authoring.mode === 'puzzle' ? authoring.puzzle.solutions.map((s) => s.word) : [];
  let moves: Move[] = [];
  let state: PlayState = createPlayState(level);
  let startedAt = Date.now();
  let status: 'open' | 'finished' = 'open';
  let finishedAt: number | null = null;
  let finishCode: PlayRecord['finishCode'] = null;
  let finalScore: number | null = null;

  const record = (): PlayRecord => {
    const now = Date.now();
    const r: PlayRecord = {
      id: PLAYTEST_ID,
      lang: level.lang,
      mode: level.mode,
      variant: level.variant,
      layer: 'daily',
      date: new Date(now).toISOString().slice(0, 10),
      levelId: level.id,
      version: level.version,
      status,
      context: null,
      startedAt,
      finishedAt,
      finishCode,
      serverTime: now,
      moves: [...moves],
    };
    if (level.mode === 'apogee') {
      r.score = { running: scorePlay(level, state.constellations).running, final: finalScore };
    } else if (level.mode === 'assemblage') {
      r.assemblage = { used: starsUsed(state), total: starsTotal(level), complete: assemblageComplete(state) };
    } else {
      const found = state.constellations.map((c) => c.word);
      r.puzzle = { blanks: blanksFor(solutions, found), complete: puzzleComplete(solutions, found) };
    }
    return r;
  };

  const finalize = (code: NonNullable<PlayRecord['finishCode']>): void => {
    status = 'finished';
    finishedAt = Date.now();
    finishCode = code;
    finalScore = level.mode === 'apogee' ? scorePlay(level, state.constellations).running : null;
  };

  return {
    reset() {
      moves = [];
      state = createPlayState(level);
      startedAt = Date.now();
      status = 'open';
      finishedAt = null;
      finishCode = null;
      finalScore = null;
    },
    async start() {
      return record();
    },
    async level() {
      return level;
    },
    async get() {
      return record();
    },
    async word(_id, nodes): Promise<WordResponse> {
      if (status !== 'open') refuse(409, 'play_finished', 'This play is finished.');
      let s: PlayState = { ...state, selection: [], selectionLines: [] };
      for (const node of nodes) {
        const outcome = tapNode(s, node);
        if (outcome.code !== 'selected') {
          refuse(409, outcome.code, `Node ${node} cannot be selected.`, outcome.word ? { word: outcome.word } : {});
        }
        s = outcome.state;
      }
      const word = spelledWord(s);
      let inDictionary = false;
      if (word) {
        try {
          inDictionary = (await api.checkWord(word, level.lang)).valid;
        } catch {
          inDictionary = false;
        }
      }
      const check = checkSubmission(s, { inDictionary, isSolution: solutions.includes(word) });
      if (check.code !== 'accepted') return { outcome: check.code, word, minWordLength: check.minWordLength, play: record() };
      const accepted = acceptSelection(s);
      moves = [...moves, { type: 'word', word, nodes: [...nodes] }];
      state = accepted.state;
      if (level.mode === 'apogee') return { outcome: 'accepted', word, score: scoreConstellation(level, accepted.constellation), play: record() };
      if (level.mode === 'assemblage') {
        if (!assemblageComplete(state)) return { outcome: 'accepted', word, play: record() };
        finalize('accepted_level_complete');
        return { outcome: 'accepted_level_complete', word, play: record() };
      }
      const blank = blankIndexFor(solutions, word);
      if (puzzleComplete(solutions, state.constellations.map((c) => c.word))) {
        finalize('accepted_level_complete');
        return { outcome: 'accepted_level_complete', word, blank, play: record() };
      }
      return { outcome: 'accepted', word, blank, play: record() };
    },
    async remove(_id, word): Promise<RemoveResponse> {
      if (status !== 'open') refuse(409, 'play_finished', 'This play is finished.');
      const outcome = removeConstellation(state, word);
      if (outcome.code !== 'removed') refuse(409, 'remove_refused', `No constellation ${word}.`, { play: record() });
      moves = [...moves, { type: 'remove', word }];
      state = outcome.state;
      return { outcome: 'removed', word, play: record() };
    },
    async finish(): Promise<FinishResponse> {
      if (level.mode === 'puzzle') refuse(409, 'puzzle_finishes_itself', 'A puzzle finishes when it is solved.');
      if (level.mode === 'assemblage') refuse(409, 'assemblage_finishes_itself', 'An Assemblage finishes when every star is used.');
      if (status !== 'open') refuse(409, 'play_finished', 'This play is finished.');
      finalize('play_finished');
      return { outcome: 'play_finished', play: record() };
    },
  };
}

/** The board as the local backend sees it, for anything that wants to check it. */
export function replayLocal(level: PlayLevel, moves: readonly Move[]): PlayState {
  return replayMoves(level, moves);
}
